import { useTranslation } from 'react-i18next'

import type { WidgetPosition } from '@/hooks/api/useWidgets'
import {
  WidgetColorRow,
  WidgetCornerPicker,
  WidgetFieldGroup,
  WidgetFieldRow,
  WidgetOffsetField,
  WidgetResetButton,
  WidgetSection,
  WidgetSegmentedControl,
} from './WidgetEditorControls'
import {
  WIDGET_BRAND_COLORS,
  WIDGET_BUTTON_SHAPES,
  WIDGET_BUTTON_SIZES,
  WIDGET_PLACEMENT_DEFAULTS,
  type WidgetStyleFormState,
} from './widgetConfig.serializers'

interface WidgetAppearanceFieldsProps {
  state: WidgetStyleFormState
  onChange: (patch: Partial<WidgetStyleFormState>) => void
}

/** The launcher's look and where it sits: everything above the toggles. */
export function WidgetAppearanceFields({ state, onChange }: WidgetAppearanceFieldsProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.appearance'

  const corners: { value: WidgetPosition; label: string; align: 'left' | 'right' }[] = [
    { value: 'bottom-left', label: t(`${base}.position_bottom-left`), align: 'left' },
    { value: 'bottom-right', label: t(`${base}.position_bottom-right`), align: 'right' },
  ]

  return (
    <>
      <WidgetSection title={t(`${base}.button_title`)}>
        <WidgetFieldGroup>
          <WidgetFieldRow label={t(`${base}.shape_label`)}>
            <WidgetSegmentedControl
              label={t(`${base}.shape_label`)}
              value={state.shape}
              options={WIDGET_BUTTON_SHAPES.map((shape) => ({
                value: shape,
                label: t(`${base}.shape_${shape}`),
              }))}
              onChange={(shape) => onChange({ shape })}
            />
          </WidgetFieldRow>

          <WidgetFieldRow label={t(`${base}.size_label`)}>
            <WidgetSegmentedControl
              label={t(`${base}.size_label`)}
              value={state.size}
              options={WIDGET_BUTTON_SIZES.map((size) => ({ value: size, label: size }))}
              onChange={(size) => onChange({ size })}
            />
          </WidgetFieldRow>
        </WidgetFieldGroup>
      </WidgetSection>

      <WidgetSection
        title={t(`${base}.colors_title`)}
        action={
          <WidgetResetButton
            label={t(`${base}.reset`)}
            title={t(`${base}.reset_colors_hint`)}
            onPress={() => onChange({ ...WIDGET_BRAND_COLORS })}
          />
        }
      >
        <WidgetFieldGroup>
          <WidgetColorRow
            id="widget-color-button"
            label={t(`${base}.color_button_label`)}
            value={state.buttonColor}
            onChange={(buttonColor) => onChange({ buttonColor })}
          />
          <WidgetColorRow
            id="widget-color-icon"
            label={t(`${base}.color_icon_label`)}
            value={state.iconColor}
            onChange={(iconColor) => onChange({ iconColor })}
          />
          <WidgetColorRow
            id="widget-color-panel"
            label={t(`${base}.color_panel_label`)}
            value={state.panelColor}
            onChange={(panelColor) => onChange({ panelColor })}
          />
          <WidgetColorRow
            id="widget-color-action"
            label={t(`${base}.color_action_label`)}
            value={state.actionColor}
            onChange={(actionColor) => onChange({ actionColor })}
          />
        </WidgetFieldGroup>
      </WidgetSection>

      <WidgetSection
        title={t(`${base}.placement_title`)}
        action={
          <WidgetResetButton
            label={t(`${base}.reset`)}
            title={t(`${base}.reset_placement_hint`)}
            onPress={() => onChange({ ...WIDGET_PLACEMENT_DEFAULTS })}
          />
        }
      >
        <WidgetFieldGroup>
          <WidgetFieldRow label={t(`${base}.corner_label`)} wide>
            <WidgetCornerPicker
              label={t(`${base}.corner_label`)}
              value={state.position}
              options={corners}
              onChange={(position) => onChange({ position })}
            />
          </WidgetFieldRow>

          <WidgetFieldRow label={t(`${base}.offset_label`)} wide>
            <div className="flex gap-2">
              <WidgetOffsetField
                id="widget-offset-x"
                axis="X"
                label={t(`${base}.offset_x_label`)}
                unit={t(`${base}.offset_unit`)}
                value={state.offsetX}
                onChange={(offsetX) => onChange({ offsetX })}
              />
              <WidgetOffsetField
                id="widget-offset-y"
                axis="Y"
                label={t(`${base}.offset_y_label`)}
                unit={t(`${base}.offset_unit`)}
                value={state.offsetY}
                onChange={(offsetY) => onChange({ offsetY })}
              />
            </div>
          </WidgetFieldRow>
        </WidgetFieldGroup>
      </WidgetSection>
    </>
  )
}
